import {AppResponse, Coupons} from './cellcast.v2.types'

interface Error {
  // Assuming empty object for now
  [key: string]: any
}
export interface ApiResponse<T> {
  app_type: string
  app_version: string
  maintainence: number
  new_version: number
  force_update: number
  invalid_token: number
  refresh_token: string
  show_message: number
  is_enc: boolean
  status: boolean
  message: string
  message_type: string
  data: T
  error: Error
}

export interface PaginateResponse<T> {
  items: T[]
  total: number
  limit: number
  current: number
  totalPages: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

export type SubscribeParams = {
  contactIDs: string[]
}

export type UnSubscribeParams = SubscribeParams

export type DeleteContactParams = {
  group: string
  allContact?: boolean
  contact_ids?: string[]
}

export interface Group {
  _id: string
  fields: string[]
  groupstatus: boolean
  tagsId: string[]
  addedThrough: string
  unSubList: boolean
  user: string
  name: string
  status: string
  mergeLabels: {newLabel: string; oldLabel: string}[]
  createdBy: string
  fieldsAndTotalLength: any[] // The type here is unknown based on the given JSON, so 'any' is used
  contacts: any[] // Same for this
  errorLog: any[] // Same for this
  createdAt: string
  updatedAt: string
  __v: number
  totalContacts: string
}

export type DefaultMessageResponse = {
  message: string
}

export type GetGroupResponse = ApiResponse<Group>

export interface CreateTagParam {
  name: string
  color?: string
}

export interface Tag {
  _id: string
  name: string
  color: string
  type: string
}

export type CreateTagResponse = ApiResponse<Tag>
export type GetTagResponse = ApiResponse<Tag[]>

export type GetListDedicatedNumberResponse = ApiResponse<
  PaginateResponse<DedicatedNumberTransaction>
>

// How-to-videos types
export interface HowToVideo {
  _id: string
  title: string
  description: string
  subTitle?: string
  subDescription?: string
  footerText?: string
  imagePath?: string
  videoUrl?: string
  category?: string
  tags?: string[]
  createdAt?: string
  updatedAt?: string
  isActive?: boolean
  sortOrder?: number
}

export interface HowToVideosRequest {
  search?: string
  category?: string
  page?: number
  size?: number
  isActive?: boolean
}

export type HowToVideosResponse = ApiResponse<PaginateResponse<HowToVideo>>

export interface DedicatedNumberTransaction {
  status: boolean
  _id: string
  purchasedNumber: string
  number: string
  email: string
  recurring: string
  ogPrice: number
  deductedPrice: number
  activationDate: string
  renewalDate: string
  stripeProductId: string
  stripeSubscrId: string
  stripeInvoiceNo: string
  stripeInvoiceNumber: string
  stripeCustomerId: string
  stripePlanId: string
  stripeInvoicePDF: string
  stripeInvoiceURL: string
  user: string
  createdAt: string
  updatedAt: string
}

export type GetListDedicatedNumberParams = {
  userId: string
  page: number
  size: number
  search: string
  duration: string
  period: string
}

export interface Scheduler {
  totalExecutions: number
  active: boolean
  status: boolean
  responseStatus: boolean
  _id: string
  name: string
  type: string
  time: Date
  message: string
  receiver: string
  user: string
  scheduleType: string
  sender: string
  fileSchedule: {
    _id: string
    name: string
    description: string
    timezone: string
  }
  queueJobId: string
  followUpQueueJobId: string
  nextExecution: Date
  createdAt: Date
  updatedAt: Date
  messageStatus: string
  gatewayMessageStatus: MessageDeliveryStatus
}

export enum MessageDeliveryStatus {
  BLOCKED = 'blocked',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  RECEIVED = 'received',
  EXPIRED = 'expired',
  QUEUED = 'queued',
  SEND = 'sent',
  REJECTED = 'rejected',
  CANCELED = 'canceled',
}

export enum UploadHistoryStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  QUEUED = 'queued',
  INPROGRESS = 'in-progress',
}

export enum SenderCamp {
  SMS = 'sms',
  MMS = 'mms',
}

type SMSDetail = {
  _id: string
  messagelength: number
  smscount: number
  costpersms: number
  deductibleCredit: number
}

type MessageSchedule = Pick<Scheduler, 'message'>

type Message = {
  to: {
    value: string[]
  }
  status: MessageDeliveryStatus
  notify: number
  isCredited: boolean
  isConfirmed: boolean
  campaign_type: string
  notification_type: string
  medium: string
  isSMS: boolean
  isMMS: boolean
  mediaUrl: string[]
  message_type: string
  rawRequestResult: string
  rawWebhookResult: string
  msgStatus: string
  isEmail_customerResponse: boolean
  isSMS_customerResponse: boolean
  replyStop: boolean
  isReplied: boolean
  workflow: boolean
  isRead: boolean
  replyStopToOptOut: boolean
  optout: boolean
  _id: string
  message: string
  user: string
  sender: string
  receiver: string
  countrycode: string
  schedule: MessageSchedule
  gateway_count: number
  longUrl: string[]
  smsDetail: SMSDetail
  request_from: string
  request_name: string
  createdBy: string
  gateway_serial: number
  messageGateway: string
  createdAt: string
  updatedAt: string
  __enc_message: boolean
  __enc_sender: boolean
  __enc_receiver: boolean
  __v: number
  send_state: string | null
  send_time: string
}

type Note = {
  _id: string
  step: number
  text: string
}

type SchedulerJob = {
  _id: string
  schedule: MessageSchedule
  note: Note[]
  executionDate: string
  executionStartTime: string
  createdAt: string
  updatedAt: string
  __v: number
  executionEndTime: string
  isSuccess: boolean
}

type UploadHistory = {
  isRead: boolean
  _id: string
  filekey: string
  user: string
  group: string
  totalContact: string
  importedContact: string
  duplicatedContact: string
  invalidContact: string
  universalUnSub: string
  tagsCount: string
  createdBy: string
  createdAt: string
  updatedAt: string
  originalName?: string
  status: UploadHistoryStatus
  __v: number
}

type WebhookSecurity = {
  type: string
  username: string
  password: string
}

type SendInvoice = {
  invoiceIds: string[]
}

type Update2FASetting = {
  _id: string
  is2FAEnabled: boolean
}

type ExportSingleInbox = {
  downloadurl: string
}

export type MessageResponse = ApiResponse<Message[]>

export type GetSchedulerListResponse = ApiResponse<PaginateResponse<Scheduler>>

export type SchedulerResponse = ApiResponse<Scheduler>

export type SchedulerLogResponse = ApiResponse<SchedulerJob[]>

export type UploadHistoryResponse = ApiResponse<PaginateResponse<UploadHistory>>

export type WriteWebhookParams = Pick<WebhookSecurity, 'username' | 'password'>

export type TestWebhookParams = {
  payload: any
  forwardUrl: string
}

export type TestWebhookResponse = ApiResponse<{
  message: string
}>

export type GlobalSettingResponse = ApiResponse<
  {
    name: string
    createdAt: string
    updatedAt: string
    value: boolean
  }[]
>

export type GlobalSettingUpdateResponse = ApiResponse<{
  data: {
    name: string
    value: boolean
    _id: string
    createdAt: string
    updatedAt: string
  }
}>

export type MongoAdminScaleResponse = ApiResponse<
  PaginateResponse<{
    _id: string
    triggerFrom: string
    action: string
    status: string
    instanceSize: string
    createdAt: string
    updatedAt: string
  }>
>

export type TriggerMongoUpdateResponse = ApiResponse<{message: string}>

export type WriteWebhookSecurityResponse = ApiResponse<DefaultMessageResponse>
export type DetailWebhookSecurityResponse = ApiResponse<WebhookSecurity>

export type SendInvoiceParams = SendInvoice
export type SendInvoiceResponse = ApiResponse<DefaultMessageResponse>

export type UpdateAccountTypeResponse = ApiResponse<DefaultMessageResponse>

export type Update2FASettingResponse = ApiResponse<Update2FASetting>

export type UpdateBillingAddressResponse = ApiResponse<DefaultMessageResponse>

export type ExportSingleInboxResponse = ApiResponse<ExportSingleInbox>

export type UpdateSenderParams = {
  sender: string
  type: SenderCamp
  region: string
}

export type DedicatedNumber = {
  status: string
  assignedUser: string
  assignedOwner: string
  billingStatus: string
  activationTime: string // Could be Date if parsing it from string is necessary
  expirationTime: string // Could be Date if parsing it from string is necessary
  availability: string
  type: string
  totalAmount: number
  isDefault: boolean
  deleted: boolean
  allocatedUsers: string[] // Array of user IDs as strings
  subClientDefaults?: {[userId: string]: boolean} // Default status per subclient user
  _id: string // ObjectId stored as string
  audNumber: string
  gateway: string // ObjectId stored as string
  country: string
  createdAt: string // Could be Date if parsing it from string is necessary
  updatedAt: string // Could be Date if parsing it from string is necessary
  purchasedBy: string // ObjectId stored as string
}

export type BusinessNumber = {
  _id: string
  isDeleted: boolean
  isDefault: boolean
  businessname: string
  user: string
  requestedAt: Date
  status: 'ACTIVE' | 'REQUESTED' | 'DENIED'
  createdBy: string
  loggedInBy: string
  createdAt: Date
  updatedAt: Date
  __v: number
  approvedAt?: Date
  approvedBy?: string
  allocatedUsers: string[]
  subClientDefaults?: {[userId: string]: boolean} // Default status per subclient user
}

export type CustomNumber = {
  _id: string
  number: string
  name: string
  user?: string
  status: 'active' | 'inactive'
  requestedAt?: Date
  approvedAt?: Date
  isDefault: boolean
  deleted: boolean
  createdBy?: string
  loggedInBy?: string
  loggedInUserRole?: string
  createdAt: Date
  updatedAt: Date
  allocatedUsers: string[]
  subClientDefaults?: {[userId: string]: boolean} // Default status per subclient user
}

export type DedicatedNumberResponse = AppResponse<DedicatedNumber[]>
export type BusinessNumberResponse = AppResponse<BusinessNumber[]>
export type CustomNumberResponse = AppResponse<CustomNumber[]>

export type UserOrderAdminSummaryResponse = ApiResponse<
  {
    _id: string
    totalAmount: number
    totalVolume: number
    totalAmountWithoutGST: number
    totalAmountCOG: number
    commission: number
    commissionRate: number
  }[]
>

export type UserOrderAdminListResponse = ApiResponse<
  PaginateResponse<{
    _id: string
    totalAmount: number
    totalVolume: number
    totalAmountWithoutGST: number
    totalAmountCOG: number
    commission: number
    commissionRate: number
    createdAt: string
    type: string
    allowedMessages: number
  }>
>

export type AppIntegrationResponse = ApiResponse<{
  message: string
}>

export type UpdateEventTypeParams = {
  payload: any
}

export type UpdateWebhookParams = {
  payload: any
}
export type CouponRate = {
  id: string
  rate: number
  type: 'sms'
  min_credit: number
  max_credit: number
  code: string
  description: string
  discountType: string
  flatRate: number
  percentageOff: number
  freeCredit: number
  status: boolean
  commisionRate: number
  staffName: string
  expiredDay: string
  expiredAt: string
  createdAt: string
}

export type UserSalesTargetResponse = ApiResponse<{
  sales_target: number
}>

// Sales Commission
export type CommissionFilter = {
  fromDate: string
  toDate: string
  userId: string
}

export interface SalesCommissionListItem {
  createdAt: string
  name: string
  email: string
  type: string
  tags: string
  coupon: string
  totalAmountWithGST: number
  totalAmountWithoutGST: number
  totalAmountCOG: number
  smsCog: number
  mmsCog: number
  volume: number
  salesId: string
  purchasedDate: string
  commission: number
  commissionRate: number
}

export interface SalesCommissionSummary {
  sale_gst: number
  sale_without_gst: number
  commission: number
  rate: number
  cog: number
  volume: number
}

export interface SalesSummaryReport {
  list: SalesCommissionListItem[]
  bellow_target: SalesCommissionSummary
  within_target: SalesCommissionSummary
  above_target: SalesCommissionSummary
  overall_total: Omit<SalesCommissionSummary, 'rate'> // overall_total does not include 'rate'
  total_amount_with_gst_sum: number
  total_amount_without_gst_sum: number
  sale_bellow_without_gst: number
  sale_within_without_gst: number
  sale_above_without_gst: number
}

export interface OptionItem {
  label: string
  value: string
}

// System Alert
export type SystemAlertResponseData = {
  _id: string
  type: string
  methods: string[]
  startAt: string
  endAt: string
  title: string
  message: string
  recipients: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
  __v: number
}

export type SystemAlertResponse = AppResponse<SystemAlertResponseData>

export type SystemAlertRequestPayload = {
  title: string
  message: string
  type: string
  methods: string[]
  recipients: string | string[]
  startAt: string
  endAt: string
}

export type SystemAlertActiveRequestParam = {
  startAt: string
  endAt: string
}

export type SystemAlertActiveResponseData = {
  type: string
  methods: string[]
  startAt: string
  endAt: string
  _id: string
  title: string
  message: string
  recipients: string[]
  createdBy: string
  createdAt: string
  updatedAt: string
  __v: number
}

export type SystemAlertActiveResponse = AppResponse<SystemAlertActiveResponseData>

// User Account
export type GetAllUserAccountQueryParams = {
  page?: number
  search?: string
  size?: number
}

export type GetAllUserAccountResponseData = {
  items: Array<{
    _id: string
    avatar: string | null
    name: string
    role: string
    firstName: string
    lastName: string
    accountType: string
    email: string
    companyName: string
    mobile: string
    isEmailVerified: boolean
    isMobileVerified: boolean
    status: string
    status2: string
    industry: string
    features: string[]
    lastLogin: string | null
    lastPurchase: string | null
    lastSend: string | null
    lastSendSource: string
    profileType: string | null
    createdAt: string
    salesType?: string
    accountManager?: {
      _id: string
      name: string
      firstName: string
      lastName: string
    }
    sales?: {
      _id: string
      name: string
      firstName: string
      lastName: string
    }
    totalPurchased: number
    ownerId: string
    smsStatus: string
    subClientAccountId: string | null
    tags: Array<{
      type: string
      _id: string
      name: string
    }>
    credit: {
      sms: number
      mms: number
    }
    parent: null | {
      _id: string
      name: string
      email: string
    }
  }>
  total: number
  limit: number
  current: number
  totalPages: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

export type GetAllUserAccountResponse = AppResponse<GetAllUserAccountResponseData>

// System Alert
export type UserSurvey = {
  name: string
  firstName: string
  lastName: string
  email: string
  survey: {
    score: number
    note: string
    createdAt: string
    updatedAt: string
  }
  createdBy: string
  createdAt: string
  updatedAt: string
  __v: number
}

export type UserSurveyGraph = {
  perDay: {total: number; date: string; average: number}[]
  summary: {total: number; average: number}[]
}

// Shared Number types
export interface SharedNumber {
  _id: string
  number: string
  gateway: string
  type: string
}

export type SharedNumberResponse = ApiResponse<PaginateResponse<SharedNumber>>

export type GetListSharedNumberParams = {
  page: number
  size: number
  searchKey?: string
}

export interface CustomerCoupons extends Omit<Coupons, 'status'> {
  status: 'applied' | 'expired' | 'unused'
  amountSpent: number
}

export interface SalesPerformanceItems {
  userId: string
  stripeCouponId: string
  code: string
  staffId: string
  commisionRate: number
  type: string
  assignedAt: string
  expiredAt: string
  createdAt: string
  provider: string

  user: string
  totalAmountWithGST: number
  totalAmountWithoutGST: number
  totalAmountCOG: number
  smsCog: number
  mmsCog: number
  volume: number
  purchasedDate: string

  coupon: string
  commission: number
  commissionRate: number

  _id: string

  cog: {
    sms: number
    mms: number
    commissionPercentage: number
  }

  features: string[]
  sales?: string
  lastLogin?: string
  inActiveMonthCount: number
  name: string
  email: string
  lastSend?: string
  notes?: string
  hasUsedCoupon: boolean

  staffName: string
  staffEmail: string

  couponStatus: string
}

export interface SalesPerformance {
  items: SalesPerformanceItems[]
  summary: {
    applied: number
    unused: number
    expired: number
    used: number
    commission: number
    accountSaved: number
    savedCommission: number
    accountAtRisk: number
    reviewSent: number
    reviewReceived: number
  }
}

export interface PotentialChurnItems {
  items: Array<{
    _id: string
    avatar: string | null
    name: string
    role: string
    firstName: string
    lastName: string
    accountType: string
    email: string
    companyName: string
    mobile: string
    status: string
    status2: string
    industry: string
    features: string[]
    lastLogin: string | null
    lastPurchase: string | null
    lastSend: string | null
    lastSendSource: string
    profileType: string | null
    createdAt: string
    salesType?: string
    staff?: {
      _id: string
      name: string
      email: string
    }
    totalPurchased: number
    potentialChurn: boolean
    hasUsedCoupon: boolean
  }>
  total: number
  limit: number
  current: number
  totalPages: number
  pagingCounter: number
  hasPrevPage: boolean
  hasNextPage: boolean
  prevPage: number | null
  nextPage: number | null
}

// Admin Dashboard Types
export interface AdminDashboardQueryParams {
  fromDate: string // YYYY-MM-DD
  toDate: string // YYYY-MM-DD
  accountId?: string
}

export interface AdminDashboardActivityOverviewData {
  totalSentSMS: {count: number}[]
  totalSentMMS: {count: number}[]
  totalSmsParts: {count: number}[]
  totalMmsParts: {count: number}[]
  modicaCOG: {total: number}[]
  sinchCOG: {total: number}[]
  telstraCOG: {total: number}[]
  newUsers: {count: number}[]
  totalSales: {totoalAmount: number}[] // Note: typo "totoal" exists in backend
  totalContacts: {count: number}[]
  totalCredits: {count: number}[]
  sinchSent: {count: number}[]
  sinchParts: {count: number}[]
  modicaSent: {count: number}[]
  modicaParts: {count: number}[]
  telstraSent: {count: number}[]
  telstraParts: {count: number}[]
}

export interface AdminDashboardSalesContactsData {
  totalSales: {totoalAmount: number}[] // Note: typo "totoal" exists in backend
  totalContacts: {count: number}[]
  totalCredits: {count: number}[]
}

export interface AdminDashboardGraphDataItem {
  _id: string
  count: number
}

export interface AdminDashboardGraphData {
  graphData: AdminDashboardGraphDataItem[]
  totalCount: {total: number}[]
}

export interface AdminDashboardBarGraphData {
  gatewaySMSData: AdminDashboardGraphData[]
  gatewayMMSData: AdminDashboardGraphData[]
  newContactData: AdminDashboardGraphData[]
}

export interface AdminDashboardLineGraphParams extends AdminDashboardQueryParams {
  graphFilter: 'qm' | 'bm' | 'api'
}

export interface AdminDashboardLineGraphData {
  gatewaySMSData: AdminDashboardGraphData[]
  gatewayMMSData: AdminDashboardGraphData[]
  totalCOGData: AdminDashboardGraphData & {graphData: {_id: string; count: number}[]}
}
